import { Settings } from '@xeetr/types/models'
import { useMemo } from 'react'
import { UseFormWatch, FormState } from 'react-hook-form'

import { PLACEHOLDER_REPLACEMENTS } from './constants'

type UsePlaceholderReplacementParams = {
  watch: UseFormWatch<Settings>
  currentTenantSpecificPart: string
}

export const usePlaceholderReplacement = ({
  watch,
  currentTenantSpecificPart,
}: UsePlaceholderReplacementParams) => {
  const tenantLogo = watch('tenant.info.logoUrl')
  const tenantName = watch('tenant.name')
  const tenantStreet = watch('tenant.info.street')
  const tenantPostcode = watch('tenant.info.postcode')
  const tenantCity = watch('tenant.info.city')

  const replacePlaceholders = useMemo(
    () => (html: string) => {
      let result = html

      // Replace tenant-specific values
      const tenantAddress = `${tenantStreet || 'Test Street'}, ${tenantPostcode || '12345'} ${tenantCity || 'Test City'}`
      result = result
        .replace(/\{\{tenantLogo\}\}/g, tenantLogo || PLACEHOLDER_REPLACEMENTS['{{tenantLogo}}'])
        .replace(/\{\{tenantName\}\}/g, tenantName || PLACEHOLDER_REPLACEMENTS['{{tenantName}}'])
        .replace(/\{\{tenantAddress\}\}/g, tenantAddress)

      // Replace tenantSpecificPart with all its nested placeholders
      const tenantSpecificPartWithReplacements = currentTenantSpecificPart.replace(
        /\{\{tenantAddress\}\}/g,
        tenantAddress
      )

      Object.entries(PLACEHOLDER_REPLACEMENTS).forEach(([key, value]) => {
        if (key !== '{{tenantLogo}}' && key !== '{{tenantName}}') {
          result = result.replace(
            new RegExp(key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'),
            value
          )
        }
      })

      result = result.replace(/\{\{tenantSpecificPart\}\}/g, tenantSpecificPartWithReplacements)

      return result
    },
    [currentTenantSpecificPart, tenantLogo, tenantName, tenantStreet, tenantPostcode, tenantCity]
  )

  return replacePlaceholders
}

export const useTemplateEditStatus = (watch: UseFormWatch<Settings>, formState: FormState<Settings>) => {
  return (bodyField: string) => {
    const currentValue = watch(`tenant.settings.${bodyField}` as any)
    const defaultValue =
      formState.defaultValues?.tenant?.settings?.[
        bodyField as keyof typeof formState.defaultValues.tenant.settings
      ]
    return !!currentValue && currentValue !== defaultValue
  }
}
